import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { AdminGate } from "@/components/AdminGate";
import { diagnoseGithubIssue } from "@/lib/github-diagnose.functions";

export const Route = createFileRoute("/admin/github-help")({
  head: () => ({
    meta: [
      { title: "GitHub Connection Helper | AutoSpecData" },
      { name: "description", content: "Describe a GitHub connection problem and get a likely cause and fix." },
      { property: "og:title", content: "GitHub Connection Helper | AutoSpecData" },
      { property: "og:description", content: "Describe a GitHub connection problem and get a likely cause and fix." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  ssr: false,
  component: () => (
    <AdminGate>
      <GithubHelp />
    </AdminGate>
  ),
});

function GithubHelp() {
  const diagnose = useServerFn(diagnoseGithubIssue);
  const [issue, setIssue] = useState("");
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (loading || issue.trim().length < 5) return;
    setLoading(true); setAnswer(""); setError("");
    try {
      const r = await diagnose({ data: { issue } });
      if (r.ok) setAnswer(r.answer); else setError(r.error);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="mx-auto max-w-2xl px-4 py-10">
      <h1 className="text-2xl font-bold text-foreground">GitHub connection helper</h1>
      <p className="mt-2 text-muted-foreground">Describe what happens when you try to connect or sync with GitHub.</p>
      <form onSubmit={submit} className="mt-6 space-y-3">
        <textarea
          value={issue}
          onChange={(e) => setIssue(e.target.value)}
          rows={6}
          maxLength={4000}
          placeholder="e.g. I clicked Connect project but the GitHub window closes and nothing happens."
          className="w-full rounded-md border border-input bg-background p-3 text-foreground"
        />
        <button
          type="submit"
          disabled={loading || issue.trim().length < 5}
          className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground disabled:opacity-50"
        >
          {loading ? "Diagnosing…" : "Diagnose"}
        </button>
      </form>
      {error && <p className="mt-6 rounded-md border border-destructive p-3 text-destructive">{error}</p>}
      {answer && (
        <div className="mt-6 whitespace-pre-wrap rounded-md border border-border bg-card p-4 text-card-foreground">{answer}</div>
      )}
    </main>
  );
}
